import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../auth-lib';

// The 180-hour monthly planning baseline, on the client side.
//
// ⚠️ Advisory ONLY. Nothing here disables a control, filters anyone out of a
// picker, or blocks a save. Its whole job is to put "164h of 180h" next to a
// name so the person assigning can decide with their eyes open — and then
// assign it to them anyway if that is the right call.

/** Every active person's booked hours for a month. */
export function useWorkload(month) {
  const [rows, setRows] = useState([]);
  const [capacityHours, setCapacityHours] = useState(180);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get('/api/tasks/workload', { params: month ? { month } : undefined })
      .then((r) => {
        setRows(r.data.rows || []);
        setCapacityHours(r.data.capacityHours || 180);
      })
      // A failure here must not break the screen it is decorating — the
      // assignment still works, it just loses the hint.
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(load, [load]);

  const byUser = new Map(rows.map((r) => [String(r.userId), r]));
  return { rows, byUser, capacityHours, loading, reload: load };
}

/** "164h / 180h" — appended to a name in a picker. */
export function workloadSuffix(row, capacityHours = 180) {
  if (!row) return '';
  return ` · ${Math.round(row.allocatedHours)}h / ${capacityHours}h`;
}

/**
 * The warning for a chosen person, or null.
 *
 * Mirrors utils/workload.js#warnIfOver on the server. Duplicated deliberately
 * rather than fetched: this has to react as the user picks, and a round trip
 * per keystroke to render a hint would be worse than the hint is worth. The
 * server is not enforcing anything either, so the two cannot fall out of step
 * in a way that matters.
 */
export function workloadWarning(row, { addingHours = 0, capacityHours = 180 } = {}) {
  if (!row) return null;
  const after = row.allocatedHours + Number(addingHours || 0);
  if (after <= capacityHours) return null;
  return row.allocatedHours > capacityHours
    ? `Already at ${Math.round(row.allocatedHours)}h of ${capacityHours}h this month.`
    : `This would take them to ${Math.round(after)}h of ${capacityHours}h this month.`;
}
